import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, BoxId, BoxSettings } from 'models/Box';
import { boxesQuery } from 'networking/mindfeed/queries/boxes';
import {
  BoxSettingsPatch,
  patchBoxSettings,
} from 'networking/mindfeed/services/box.service';
import { isContext } from 'utils/ContextUtils';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';

type Context = { previousBoxes: Box[] | undefined };

export type Variables = {
  boxId: BoxId;
  patch: BoxSettingsPatch;
};

// Flips a box's comments or reactions switch. The box list shows the change
// straight away, then reloads from the server, which also re-derives any
// switch the owner never touched.
export const useUpdateBoxSettings = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (settings: BoxSettings) => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { selectedSpaceId } = useActiveSpace();
  const boxesKey = boxesQuery(selectedSpaceId).queryKey;

  return useMutation<BoxSettings, Error, Variables, Context>({
    mutationFn: ({ boxId, patch }) => patchBoxSettings(boxId, patch),
    onMutate: async ({ boxId, patch }) => {
      // Don't let an in-flight refetch overwrite the optimistic switch.
      await queryClient.cancelQueries({ queryKey: boxesKey });
      const previousBoxes = queryClient.getQueryData<Box[]>(boxesKey);

      queryClient.setQueryData<Box[]>(boxesKey, (boxes) =>
        boxes?.map((box) =>
          box.id === boxId
            ? { ...box, settings: { ...box.settings, ...patch } }
            : box,
        ),
      );

      return { previousBoxes };
    },
    onSuccess: (settings) => onSuccess?.(settings),
    onError: (_error, _variables, context) => {
      if (isContext<Context>(context, 'previousBoxes')) {
        queryClient.setQueryData<Box[]>(boxesKey, context.previousBoxes);
      }
      onError?.();
    },
    // Whether it worked or not, reload the list so it matches the server.
    onSettled: () => queryClient.invalidateQueries({ queryKey: boxesKey }),
  });
};
